import { HTMLAttributes } from 'react'
import { GripVertical } from 'lucide-react'
import { useSortable } from '@dnd-kit/sortable'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import { shapeDisplayLabel } from '../shapes/shapeLabels'
import ShapeThumbnail from './ShapeThumbnail'

type Props = {
  shape: VisualShape
  isSelected: boolean
  canEdit: boolean
  aspect: number
  entityRange?: string
  onSelect: () => void
}

const ShapeListRow = ({
  shape,
  isSelected,
  canEdit,
  aspect,
  entityRange,
  onSelect,
}: Props) => {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: shape.id, disabled: !canEdit })

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex items-center gap-3 rounded-md border p-2 transition-colors cursor-pointer',
        isSelected
          ? 'border-primary bg-accent'
          : 'border-border bg-card hover:bg-accent/50',
        isDragging && 'opacity-60',
      )}
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
      }}
      onClick={onSelect}
    >
      {canEdit ? (
        <span
          ref={setActivatorNodeRef}
          className='shrink-0 cursor-grab text-muted-foreground touch-none'
          {...(attributes as HTMLAttributes<HTMLSpanElement>)}
          {...(listeners as HTMLAttributes<HTMLSpanElement>)}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className='h-4 w-4' />
        </span>
      ) : null}
      <ShapeThumbnail shape={shape} aspect={aspect} />
      <div className='flex min-w-0 flex-1 items-center gap-2'>
        <span className='min-w-0 flex-1 truncate text-sm'>
          {shape.name?.trim() || shapeDisplayLabel(shape)}
        </span>
        {entityRange ? (
          <Badge variant='outline' className='shrink-0 text-xs'>
            {entityRange}
          </Badge>
        ) : null}
      </div>
    </div>
  )
}

export default ShapeListRow
